<template>
    <div class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h2>成长基金变动记录</h2>
                <button class="close-button" @click="closeModal">&times;</button>
            </div>
            <div class="modal-body">
                <div class="tabs">
                    <button :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'"
                        class="tab-button">
                        变动记录
                    </button>
                    <button :class="{ active: activeTab === 'analysis' }" @click="activeTab = 'analysis'"
                        class="tab-button">
                        任务分析
                    </button>
                </div>

                <div v-show="activeTab === 'history'">
                    <div v-if="worthHistory.length === 0" class="empty-state">
                        暂无价值变动记录
                    </div>
                    <div v-else class="history-list">
                        <div v-for="record in worthHistory" :key="record.id" class="history-item">
                            <div class="history-info">
                                <span class="reason">{{ record.worth_reason }}</span>
                                <span class="time">{{ formatTime(record.worth_time) }}</span>
                            </div>
                            <div class="history-value" :class="record.worth_type">
                                {{ record.worth_type === 'add' ? '+' : '' }}{{ record.worth_change }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="activeTab === 'analysis'" class="analysis-content">
                    <div ref="chartContainer" class="chart-container"></div>
                    <div v-if="chartData.length === 0" class="empty-chart">
                        暂无数据分析
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import * as echarts from 'echarts'
import { useTodoStore } from '../stores/todoStore.js'

export default {
    name: 'WorthHistoryModal',
    setup() {
        const store = useTodoStore()
        const activeTab = ref('history')
        const chartContainer = ref(null)
        let chartInstance = null

        const worthHistory = computed(() => store.worthHistory)

        // 计算图表数据
        const chartData = computed(() => {
            if (!worthHistory.value.length || !store.tasks.length || !store.types.length) {
                return []
            }

            // 创建类型ID到标题的映射
            const typeMap = {}
            store.types.forEach(type => {
                typeMap[type.id] = type.name
            })

            // 创建任务ID到类型ID的映射
            const taskTypeMap = {}
            store.tasks.forEach(task => {
                taskTypeMap[task.id] = task.typeId
            })

            // 按类型聚合价值变动
            const typeValues = {}
            worthHistory.value.forEach(record => {
                // 遍历关联的任务项
                if (record.todo_ids && record.todo_ids.length > 0) {
                    record.todo_ids.forEach(todoId => {
                        const typeId = taskTypeMap[todoId]
                        if (typeId) {
                            if (!typeValues[typeId]) {
                                typeValues[typeId] = 0
                            }
                            // 根据变动类型处理值
                            const value = parseFloat(record.worth_change) || 0
                            // 对于增加类型，直接加；对于扣除类型，减去绝对值
                            typeValues[typeId] += record.worth_type === 'add' ? value : -Math.abs(value)
                        }
                    })
                }
            })

            // 转换为图表所需格式
            return Object.keys(typeValues).map(typeId => ({
                name: typeMap[typeId] || `类型${typeId}`,
                value: Math.abs(typeValues[typeId]) // 使用绝对值显示
            })).filter(item => item.value > 0) // 只显示有值的项
        })

        const closeModal = () => {
            store.closeWorthHistoryModal()
        }

        const formatTime = (timeString) => {
            if (!timeString) return ''
            const date = new Date(timeString)
            return date.toLocaleString('zh-CN')
        }

        // 初始化图表
        const initChart = () => {
            console.log('Initializing chart, container exists:', !!chartContainer.value)
            if (chartContainer.value) {
                console.log('Chart container dimensions:', chartContainer.value.offsetWidth, 'x', chartContainer.value.offsetHeight)
                try {
                    if (chartInstance) {
                        chartInstance.dispose()
                    }
                } catch (e) {
                    console.warn('Error disposing existing chart:', e)
                }

                try {
                    chartInstance = echarts.init(chartContainer.value)
                    console.log('Chart initialized successfully')
                    updateChart()
                } catch (e) {
                    console.error('Error initializing chart:', e)
                }
            } else {
                console.warn('Chart container not found')
            }
        }

        // 更新图表
        const updateChart = () => {
            console.log('Updating chart, instance exists:', !!chartInstance)
            if (!chartInstance) return

            try {
                const option = {
                    title: {
                        text: '任务价值分布',
                        left: 'center'
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: '{a} <br/>{b}: {c} ({d}%)'
                    },
                    legend: {
                        orient: 'vertical',
                        left: 'left'
                    },
                    series: [
                        {
                            name: '价值分布',
                            type: 'pie',
                            radius: '50%',
                            data: chartData.value,
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }
                    ]
                }

                chartInstance.setOption(option, { notMerge: true })
                console.log('Chart updated successfully')
            } catch (e) {
                console.error('Error updating chart:', e)
            }
        }

        // 强制刷新图表
        const refreshChart = () => {
            if (activeTab.value === 'analysis' && chartContainer.value) {
                try {
                    if (chartInstance) {
                        chartInstance.dispose()
                        chartInstance = null
                    }
                } catch (e) {
                    console.warn('Error disposing chart in refresh:', e)
                }
                initChart()
            }
        }

        // 监听标签页切换
        watch(activeTab, (newTab) => {
            console.log('Tab switched to:', newTab)
            if (newTab === 'analysis') {
                console.log('Switching to analysis tab')
                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick executed, container exists:', !!chartContainer.value)
                    if (chartContainer.value) {
                        console.log('Container dimensions:', chartContainer.value.offsetWidth, 'x', chartContainer.value.offsetHeight)
                        // 延迟一小段时间确保DOM完全渲染
                        setTimeout(() => {
                            console.log('Delayed init after 50ms')
                            // 如果图表实例不存在，重新初始化
                            if (!chartInstance) {
                                console.log('Chart instance not found, initializing')
                                initChart()
                            } else {
                                // 如果图表实例存在，确保其大小正确并更新数据
                                try {
                                    console.log('Chart instance exists, resizing and updating')
                                    chartInstance.resize()
                                    updateChart()
                                } catch (e) {
                                    console.warn('Error resizing/updating chart:', e)
                                    // 如果出现错误，重新初始化图表
                                    console.log('Error occurred, reinitializing chart')
                                    initChart()
                                }
                            }
                        }, 50)
                    } else {
                        console.warn('Chart container not found when switching to analysis tab')
                    }
                })
            } else if (newTab === 'history' && chartInstance) {
                console.log('Switching to history tab')
                // 当切换到历史记录标签页时，确保图表被正确清理
                try {
                    if (chartInstance) {
                        chartInstance.resize() // 调整大小以适应隐藏状态
                    }
                } catch (e) {
                    console.warn('Error resizing chart when switching to history tab:', e)
                }
            }
        })

        // 监听数据变化，更新图表
        watch(chartData, () => {
            console.log('Chart data changed, active tab:', activeTab.value)
            if (activeTab.value === 'analysis') {
                nextTick(() => {
                    // 确保DOM已经更新
                    console.log('Next tick in chart data watcher, container exists:', !!chartContainer.value)
                    if (chartContainer.value) {
                        // 如果图表实例不存在，重新初始化
                        if (!chartInstance) {
                            console.log('Chart instance not found in data watcher, initializing')
                            initChart()
                        } else {
                            // 如果图表实例存在，更新数据
                            console.log('Chart instance exists in data watcher, updating')
                            updateChart()
                        }
                    }
                })
            }
        })

        // 组件挂载时初始化
        onMounted(() => {
            console.log('Component mounted')
            nextTick(() => {
                console.log('Next tick in mounted hook, active tab:', activeTab.value)
                // 延迟初始化以确保DOM完全准备好
                setTimeout(() => {
                    console.log('Delayed init in mounted hook, container exists:', !!chartContainer.value)
                    if (activeTab.value === 'analysis' && chartContainer.value) {
                        console.log('Initializing chart on mount')
                        initChart()
                    }
                }, 100)

                // 监听窗口大小变化
                if (typeof window !== 'undefined') {
                    window.addEventListener('resize', () => {
                        if (chartInstance && chartContainer.value) {
                            chartInstance.resize()
                        }
                    })
                }
            })
        })

        // 组件卸载时清理
        onUnmounted(() => {
            if (chartInstance) {
                try {
                    chartInstance.dispose()
                } catch (e) {
                    console.warn('Error disposing chart:', e)
                }
                chartInstance = null
            }
        })

        return {
            worthHistory,
            chartData,
            chartContainer,
            activeTab,
            closeModal,
            formatTime,
            refreshChart
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 800px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header {
    padding: 20px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: #999;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-button:hover {
    color: #333;
}

.tabs {
    display: flex;
    border-bottom: 1px solid #eee;
}

.tab-button {
    padding: 12px 20px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1rem;
    color: #666;
    border-bottom: 2px solid transparent;
}

.tab-button:hover {
    background-color: #f5f5f5;
}

.tab-button.active {
    color: #409eff;
    border-bottom-color: #409eff;
}

.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.empty-state {
    text-align: center;
    color: #999;
    padding: 40px 20px;
}

.history-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-radius: 6px;
    background-color: #f8f9fa;
    transition: background-color 0.2s;
}

.history-item:hover {
    background-color: #e9ecef;
}

.history-info {
    flex: 1;
}

.reason {
    display: block;
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
}

.time {
    display: block;
    font-size: 0.85rem;
    color: #666;
}

.history-value {
    font-weight: bold;
    font-size: 1.1rem;
    padding: 6px 12px;
    border-radius: 4px;
}

.history-value.add {
    background-color: #d4edda;
    color: #155724;
}

.history-value.subtract {
    background-color: #f8d7da;
    color: #721c24;
}

.analysis-content {
    min-height: 400px;
}

.chart-container {
    width: 100%;
    height: 400px;
    /* 确保容器在隐藏时也能保持尺寸 */
    min-width: 200px;
    min-height: 200px;
}

.empty-chart {
    text-align: center;
    color: #999;
    padding: 40px 20px;
}
</style>